import { cn } from '@/lib/utils';
import { classificationKeys, methodKeys } from '@utils/verificationLabels';
import type { VerificationItem } from '../../../types/verification';

export const TOTAL_COLUMNS = 4 + classificationKeys.length + 1 + methodKeys.length + 2;

/** Everything to the right of the two sticky unit columns. */
export const UNIT_BODY_COLUMNS = 2 + classificationKeys.length + 1 + methodKeys.length + 2;

/**
 * The palette is literal rather than themed on purpose: it reproduces the
 * printed Mazak 設計検証項目一覧 form that reviewers cross-check against, where
 * the fill colour is how a column is identified.
 */
const HEADER_BASE =
  'sticky top-0 z-[2] h-auto whitespace-nowrap border-b border-b-[#e0e0e0] border-r border-r-white/20 px-2 py-1.5 text-[0.7rem] font-bold';

export const headerCell = `${HEADER_BASE} bg-[#424242] text-white`;
export const classHeaderCell = `${HEADER_BASE} bg-[#e0f7fa] text-black`;
export const methodHeaderCell = `${HEADER_BASE} bg-[#e8f5e9] text-black`;

/**
 * No + Unit are the only sticky columns, to spend as little width as possible.
 * The offsets are written out because Tailwind scans source text — an
 * interpolated `left-[${WIDTH}px]` would never be generated.
 */
export const stickyCol0 = 'sticky left-0 z-[3] w-[32px] min-w-[32px]';
export const stickyCol1 =
  'sticky left-[32px] z-[3] w-[72px] min-w-[72px] border-r-2 border-r-black/20';

/** Sticky on both axes, so it has to outrank the row-1 and column-0 planes. */
export const stickyHeaderCorner = 'z-[4]';

export const verticalHeaderCell = 'w-[28px] min-w-[28px] max-w-[28px] overflow-hidden px-0 py-1 text-center';

export const verticalHeaderText =
  'revy-cjk mx-auto flex h-16 items-center justify-center whitespace-nowrap text-[0.6rem] leading-[1.5] [text-orientation:mixed] [writing-mode:vertical-rl]';

const CELL_BASE = 'border-b border-b-[#e0e0e0]';

// Only the merged unit and spec cells were top-aligned; data cells inherited
// MUI's middle alignment, which keeps the marks and actions registered with the
// text they annotate on a multi-line row.
const MERGED_CELL = `${CELL_BASE} align-top`;

export const unitCell = `${MERGED_CELL} border-r border-r-black/[0.12] bg-[#ccffcc] px-4 py-1.5 text-xs font-bold`;
export const specCell = `${MERGED_CELL} border-r border-r-black/[0.12] bg-[#ffffcc] px-4 py-1.5 text-xs`;
export const dataCell = `${CELL_BASE} border-r border-r-black/[0.06] bg-white p-1 text-xs`;
export const markCell = cn(dataCell, 'min-w-[28px] max-w-[28px] px-0 text-center');

export interface SpecGroup {
  spec: string;
  items: VerificationItem[];
}

/** Consecutive items sharing a specification render under one merged cell. */
export function groupItemsBySpec(items: VerificationItem[]): SpecGroup[] {
  const groups: SpecGroup[] = [];
  for (const item of items) {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.spec === item.specification) {
      lastGroup.items.push(item);
    } else {
      groups.push({ spec: item.specification, items: [item] });
    }
  }
  return groups;
}
